import React, { useState, useEffect } from "react";
import {
  Car, Plus, Upload, Hash, MapPin, Users, Settings2, Fuel, Pencil, Trash2,
  CheckCircle2, CreditCard, Banknote, TrendingUp, ArrowUpRight, ArrowRight,
  Mail, Phone, Search, FileText, ShieldCheck, Camera, ClipboardCheck, Sparkles,
  LayoutGrid, CalendarDays, Bell, AlertTriangle,
} from "lucide-react";

/* ===========================
   Hero — Grand tableau de bord Pilotage
   =========================== */
export function HeroDashboardMockup() {
  const kpis = [
    { label: "Revenus du mois", value: "12 450 €", sub: "+18 % vs mai", subCls: "text-emerald-600", icon: Banknote },
    { label: "Taux d'occupation", value: "72 %", sub: "14 véhicules", subCls: "text-slate-400", icon: TrendingUp },
    { label: "Locations en cours", value: "9", sub: "3 retours aujourd'hui", subCls: "text-slate-400", icon: Car },
    { label: "Tâches urgentes", value: "3", sub: "À traiter ce matin", subCls: "text-red-500", icon: AlertTriangle, urgent: true },
  ];
  const gantt = [
    { name: "Tesla Model 3", bars: [{ l: 6, w: 36, cls: "bg-violet-600 resa-glow", label: "S. Laurent", light: true }, { l: 62, w: 26, cls: "bg-slate-200", label: "T. Morel" }] },
    { name: "Mercedes GLC", bars: [{ l: 0, w: 22, cls: "bg-slate-200", label: "A. Petit" }, { l: 44, w: 28, cls: "bg-violet-200", label: "M. Diallo" }] },
    { name: "BMW Série 5", bars: [{ l: 26, w: 40, cls: "bg-violet-300", label: "C. Robin" }] },
  ];
  const tasks = [
    { label: "Contrat à signer — J. Martin", tag: "Urgent", tagCls: "bg-red-100 text-red-600 task-glow-red", icon: FileText },
    { label: "État des lieux retour — GLC", tag: "11h00", tagCls: "bg-violet-100 text-violet-700", icon: Camera },
    { label: "Devis à envoyer — Sté Kariba", tag: "Aujourd'hui", tagCls: "bg-amber-100 text-amber-700", icon: Mail },
  ];
  return (
    <div className="mockup-frame w-full">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50">
        <span className="chrome-dots" />
        <div className="ml-3 flex-1 text-[10px] font-mono text-slate-400 truncate"></div>
        <span className="hidden sm:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-100 text-[9px] font-bold text-emerald-700">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> En direct
        </span>
      </div>
      <div className="flex">
        {/* Sidebar */}
        <div className="hidden md:flex flex-col items-center gap-4 py-5 px-3 border-r border-slate-100 bg-slate-50/60">
          <span className="h-9 w-9 rounded-xl bg-violet-600 text-white flex items-center justify-center"><LayoutGrid className="h-4 w-4" /></span>
          <span className="h-9 w-9 rounded-xl text-slate-400 flex items-center justify-center"><CalendarDays className="h-4 w-4" /></span>
          <span className="h-9 w-9 rounded-xl text-slate-400 flex items-center justify-center"><Car className="h-4 w-4" /></span>
          <span className="h-9 w-9 rounded-xl text-slate-400 flex items-center justify-center"><Users className="h-4 w-4" /></span>
          <span className="h-9 w-9 rounded-xl text-slate-400 flex items-center justify-center"><Settings2 className="h-4 w-4" /></span>
        </div>
        {/* Contenu */}
        <div className="flex-1 p-4 md:p-6 mockup-enter">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-heading text-base md:text-lg font-semibold text-slate-900">Bonjour Marc</div>
              <div className="text-[11px] text-slate-500">Mardi 10 juin · Voici votre journée</div>
            </div>
            <span className="relative h-9 w-9 rounded-xl border border-slate-200 bg-white flex items-center justify-center">
              <Bell className="h-4 w-4 text-slate-500" />
              <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-white text-[8px] font-bold flex items-center justify-center task-glow-red">3</span>
            </span>
          </div>
          {/* KPI */}
          <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {kpis.map((k) => (
              <div key={k.label} className={`rounded-2xl border p-3.5 ${k.urgent ? "border-red-200 bg-red-50/50" : "border-slate-200 bg-white"}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-500">{k.label}</span>
                  <k.icon className={`h-3.5 w-3.5 ${k.urgent ? "text-red-500" : "text-violet-500"}`} />
                </div>
                <div className="mt-1.5 font-heading text-lg md:text-xl font-semibold text-slate-900">{k.value}</div>
                <div className={`text-[10px] font-medium ${k.subCls}`}>{k.sub}</div>
              </div>
            ))}
          </div>
          {/* Planning + Tâches */}
          <div className="mt-4 grid lg:grid-cols-5 gap-3">
            <div className="lg:col-span-3 rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-slate-900">Planning de la semaine</span>
                <span className="text-[9px] font-bold text-violet-600 uppercase tracking-wider">Temps réel</span>
              </div>
              <div className="space-y-2.5">
                {gantt.map((r) => (
                  <div key={r.name} className="grid grid-cols-[80px_1fr] gap-2 items-center">
                    <div className="text-[10px] font-medium text-slate-600 truncate">{r.name}</div>
                    <div className="relative h-8 rounded-lg bg-slate-50 border border-slate-100">
                      <div aria-hidden className="absolute inset-y-0 bg-violet-50/80" style={{ left: "28.57%", width: "14.28%" }} />
                      {r.bars.map((b) => (
                        <div key={b.label} className={`absolute top-1 bottom-1 rounded-md ${b.cls} flex items-center px-1.5`} style={{ left: `${b.l}%`, width: `${b.w}%` }}>
                          <span className={`text-[8px] font-semibold truncate ${b.light ? "text-white" : "text-slate-700"}`}>{b.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-4">
              <div className="text-xs font-semibold text-slate-900 mb-3">Tâches du jour</div>
              <div className="space-y-2">
                {tasks.map((t) => (
                  <div key={t.label} className="flex items-center gap-2.5 rounded-xl border border-slate-100 bg-slate-50/60 px-2.5 py-2">
                    <t.icon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <span className="flex-1 text-[10px] font-medium text-slate-700 truncate">{t.label}</span>
                    <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-bold shrink-0 ${t.tagCls}`}>{t.tag}</span>
                  </div>
                ))}
                <div className="flex items-center gap-2.5 rounded-xl border border-emerald-100 bg-emerald-50/60 px-2.5 py-2">
                  <CreditCard className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                  <span className="flex-1 text-[10px] font-medium text-slate-700 truncate">Paiement reçu — S. Laurent</span>
                  <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[8px] font-bold shrink-0">+516 €</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===========================
   Hero — Fleet board (gestion flotte compacte)
   =========================== */
export function FleetBoardMockup() {
  const rows = [
    { name: "Tesla Model 3", pct: 92, status: "Loué", statusCls: "bg-violet-100 text-violet-700" },
    { name: "Mercedes Classe S", pct: 78, status: "Loué", statusCls: "bg-violet-100 text-violet-700" },
    { name: "BMW Série 5", pct: 54, status: "Dispo", statusCls: "bg-emerald-100 text-emerald-700" },
  ];
  return (
    <div className="mockup-frame w-full max-w-[640px] mx-auto">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50">
        <span className="chrome-dots" />
        <div className="ml-3 flex-1 text-[10px] font-mono text-slate-400 truncate"></div>
      </div>
      <div className="p-4 mockup-enter">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-violet-600">Flotte</div>
            <div className="font-heading text-base font-medium text-slate-900">Mes véhicules</div>
          </div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-violet-600 text-white text-[10px] font-semibold">
            <Plus className="h-3 w-3" /> Ajouter
          </span>
        </div>

        <div className="mt-3 relative h-28 rounded-2xl overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=600&q=60"
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent" />
          <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-bold">
            <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> En location
          </span>
          <div className="absolute bottom-2.5 left-3 right-3 flex items-end justify-between">
            <div>
              <div className="text-white text-xs font-semibold">Porsche 911 Carrera</div>
              <div className="text-[9px] text-slate-300 font-mono">AA-123-BB · retour ven. 12</div>
            </div>
            <div className="text-white font-heading text-sm font-semibold">459 €/j</div>
          </div>
        </div>

        <div className="mt-3 space-y-2.5">
          {rows.map((v, i) => (
            <div key={v.name} className="animate-stagger-row" style={{ "--i": i + 1 }}>
              <div className="flex items-center justify-between text-[10px]">
                <span className="font-medium text-slate-900">{v.name}</span>
                <span className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-bold ${v.statusCls}`}>{v.status}</span>
                  <span className="font-semibold text-slate-900 w-7 text-right">{v.pct}%</span>
                </span>
              </div>
              <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-violet-600 to-violet-400 booking-bar"
                  style={{ width: `${v.pct}%`, animationDelay: `${350 + i * 180}ms` }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-3 flex items-center justify-between rounded-xl bg-violet-50 border border-violet-100 px-3 py-2">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-violet-800">
            <TrendingUp className="h-3 w-3" /> Occupation flotte : 75 %
          </span>
          <span className="text-[9px] font-bold text-emerald-600">+18 % vs mois dernier</span>
        </div>
      </div>
    </div>
  );
}

/* ===========================
   1. Gestion des véhicules
   =========================== */
export function VehicleAddMockup() {
  const cycles = [
    { name: "Porsche 911 Carrera", price: "459 €" },
    { name: "Range Rover Sport", price: "389 €" },
    { name: "Audi RS6 Avant", price: "429 €" },
    { name: "Mercedes AMG GT", price: "549 €" },
  ];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % cycles.length), 5600);
    return () => clearInterval(id);
  }, [cycles.length]);
  const active = cycles[idx];
  return (
    <div className="relative mockup-frame w-full max-w-[640px] mx-auto">
      {/* Browser chrome */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50">
        <span className="chrome-dots" />
        <div className="ml-3 flex-1 text-[10px] font-mono text-slate-400 truncate"></div>
      </div>

      {/* Dashboard header */}
      <div className="p-5 mockup-enter">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-violet-600">Gestion</div>
            <div className="font-heading text-lg font-medium text-slate-900">Mes véhicules</div>
            <div className="text-xs text-slate-500">4 véhicules · Occupation moyenne 72%</div>
          </div>
          <button className="animate-badge-breathe inline-flex items-center gap-2 px-3 py-2 rounded-full bg-violet-600 text-white text-xs font-semibold">
            <Plus className="h-3.5 w-3.5" /> Ajouter un véhicule
          </button>
        </div>

        {/* Vehicle list (compact rows) */}
        <div className="mt-4 space-y-2">
          {[
            { name: "Tesla Model 3", plate: "AA-123-BB", price: "129 €/j", photo: "https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=200&q=60" },
            { name: "Mercedes Classe S", plate: "CC-456-DD", price: "289 €/j", photo: "https://images.unsplash.com/photo-1728236436940-10b097998033?w=200&q=60" },
            { name: "BMW Série 5", plate: "EE-789-FF", price: "189 €/j", photo: "https://images.unsplash.com/photo-1761040100488-469dacc9e1c1?w=200&q=60" },
          ].map((v, i) => (
            <div
              key={v.name}
              className="flex items-center gap-3 p-2.5 rounded-xl border border-slate-100 bg-white animate-stagger-row"
              style={{ "--i": i + 1 }}
            >
              <div className="h-10 w-14 rounded-lg bg-slate-100 overflow-hidden shrink-0">
                <img src={v.photo} alt="" className="h-full w-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-medium text-slate-900 truncate">{v.name}</div>
                <div className="inline-flex items-center gap-1 text-[9px] font-mono text-slate-500"><Hash className="h-2.5 w-2.5" /> {v.plate}</div>
              </div>
              <div className="text-[11px] font-semibold text-slate-900">{v.price}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Floating modal overlay */}
      <div className="absolute -bottom-6 -right-6 w-[280px] glass-card rounded-2xl p-4" style={{ animation: "scaleIn 500ms 400ms cubic-bezier(0.22,1,0.36,1) both" }}>
        <div className="flex items-center justify-between">
          <div className="font-heading text-sm font-medium text-slate-900">Nouveau véhicule</div>
          <div className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 text-xs">×</div>
        </div>
        <div className="mt-3 space-y-2">
          <Field key={`n-${idx}`} label="Nom" value={active.name} />
          <Field key={`p-${idx}`} label="Prix / jour" value={active.price} />
          <div>
            <div className="text-[8px] tracking-[0.2em] uppercase font-bold text-slate-500">Photo</div>
            <div className="mt-1 h-16 rounded-lg bg-gradient-to-br from-violet-100 to-slate-100 border border-dashed border-slate-300 flex items-center justify-center text-[10px] text-slate-500 relative overflow-hidden animate-shimmer-bar">
              <Upload className="h-3 w-3 mr-1" /> Importer une image
            </div>
          </div>
          <button className="w-full px-3 py-1.5 rounded-lg bg-violet-600 text-white text-[10px] font-semibold">
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[8px] tracking-[0.2em] uppercase font-bold text-slate-500">{label}</div>
      <div className="mt-1 h-7 rounded-lg border border-slate-200 bg-white px-2 flex items-center text-[10px] font-medium text-slate-900">
        <span className="animate-flip-up inline-block">{value}</span>
        <span className="inline-block w-[1px] h-3 bg-violet-600 ml-1 animate-pulse" />
      </div>
    </div>
  );
}

/* ===========================
   2. Calendrier des réservations
   =========================== */
export function CalendarMockup() {
  const days = Array.from({ length: 35 }, (_, i) => i + 1);
  const currentIdx = 10;
  // bookings: start, length, row (0=top), color
  const bookings = [
    { start: 2, length: 3, color: "bg-emerald-200 text-emerald-900 border-emerald-400", label: "Jean M." },
    { start: 8, length: 2, color: "bg-violet-200 text-violet-900 border-violet-400", label: "Léa F." },
    { start: 14, length: 4, color: "bg-amber-200 text-amber-900 border-amber-400", label: "Marc D." },
    { start: 19, length: 2, color: "bg-emerald-200 text-emerald-900 border-emerald-400", label: "Sarah P." },
    { start: 24, length: 3, color: "bg-violet-200 text-violet-900 border-violet-400", label: "Kevin L." },
    { start: 29, length: 2, color: "bg-emerald-200 text-emerald-900 border-emerald-400", label: "Nora B." },
  ];
  return (
    <div className="mockup-frame w-full max-w-[640px] mx-auto">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50">
        <span className="chrome-dots" />
        <div className="ml-3 flex-1 text-[10px] font-mono text-slate-400 truncate"></div>
      </div>
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-violet-600">Planning</div>
            <div className="font-heading text-lg font-medium text-slate-900 capitalize">Mars 2026</div>
          </div>
          <div className="flex items-center gap-1 p-1 rounded-lg bg-slate-100 text-[10px] font-medium">
            <span className="px-2 py-1 rounded-md bg-white shadow-sm">Mois</span>
            <span className="px-2 py-1 text-slate-500">Semaine</span>
          </div>
        </div>

        {/* Weekdays */}
        <div className="grid grid-cols-7 text-[9px] uppercase font-bold tracking-wider text-slate-400 mb-2">
          {["Lu","Ma","Me","Je","Ve","Sa","Di"].map((d) => <div key={d} className="px-1">{d}</div>)}
        </div>

        {/* Grid */}
        <div className="relative grid grid-cols-7 gap-1 auto-rows-[46px]">
          {days.map((d) => (
            <div
              key={d}
              className={`rounded-md border border-slate-100 p-1 ${d === currentIdx ? "bg-violet-600 border-violet-600" : "bg-white"}`}
            >
              <div className={`text-[10px] font-semibold ${d === currentIdx ? "text-white" : "text-slate-600"}`}>{d}</div>
            </div>
          ))}

          {/* Booking bars positioned over the grid */}
          <div className="absolute inset-0 pointer-events-none">
            {bookings.map((b, i) => {
              const row = Math.floor((b.start - 1) / 7);
              const col = (b.start - 1) % 7;
              const maxLen = Math.min(b.length, 7 - col);
              return (
                <div
                  key={`bar-${b.client}-${b.start}-${b.length}`}
                  className={`booking-bar absolute h-4 rounded-md border text-[9px] font-medium px-1.5 flex items-center ${b.color}`}
                  style={{
                    top: `calc(${row} * (46px + 4px) + 22px)`,
                    left: `calc(${col} * (100% / 7) + 2px)`,
                    width: `calc(${maxLen} * (100% / 7) - 4px)`,
                    animationDelay: `${200 + i * 120}ms`,
                  }}
                >
                  {b.label}
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        <div className="mt-4 flex items-center gap-3 text-[9px] text-slate-500">
          <Legend color="bg-emerald-500" label="Confirmée" />
          <Legend color="bg-violet-500" label="En attente" />
          <Legend color="bg-amber-500" label="À venir" />
        </div>
      </div>
    </div>
  );
}
function Legend({ color, label }) {
  return <span className="inline-flex items-center gap-1"><span className={`h-1.5 w-1.5 rounded-full ${color}`} /> {label}</span>;
}

/* ===========================
   3. Dashboard revenus
   =========================== */
export function RevenueMockup() {
  // points for area chart
  const pts = [10, 18, 14, 22, 30, 28, 38, 46, 44, 58, 66, 72];
  const max = Math.max(...pts);
  const w = 420, h = 140;
  const step = w / (pts.length - 1);
  const toY = (v) => h - (v / max) * (h - 20) - 10;
  const linePath = pts.map((v, i) => `${i === 0 ? "M" : "L"} ${i * step} ${toY(v)}`).join(" ");
  const areaPath = `${linePath} L ${w} ${h} L 0 ${h} Z`;
  return (
    <div className="mockup-frame w-full max-w-[640px] mx-auto">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50">
        <span className="chrome-dots" />
        <div className="ml-3 flex-1 text-[10px] font-mono text-slate-400 truncate"></div>
      </div>
      <div className="p-5 mockup-enter">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-violet-600">Revenus</div>
            <div className="font-heading text-lg font-medium text-slate-900">Suivi financier</div>
          </div>
          <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold">
            <ArrowUpRight className="h-3 w-3" /> + 38% ce mois
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-2 mt-4">
          <Kpi label="Brut" value="8 420 €" tone="default" />
          <Kpi label="Charges" value="-1 010 €" tone="muted" />
          <LiveNetKpi />
        </div>

        {/* Chart */}
        <div className="mt-4 rounded-xl border border-slate-100 p-3 bg-slate-50/50">
          <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-[140px]">
            <defs>
              <linearGradient id="grad-revenue" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#7c3aed" stopOpacity="0.35" />
                <stop offset="1" stopColor="#7c3aed" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={areaPath} fill="url(#grad-revenue)" />
            <path
              d={linePath}
              fill="none"
              stroke="#7c3aed"
              strokeWidth="2.5"
              strokeLinecap="round"
              style={{ strokeDasharray: 1000, animation: "drawLine 1500ms cubic-bezier(0.22,1,0.36,1) forwards" }}
            />
            {pts.map((v, i) => (
              <circle
                key={`pt-${i}-${v}`}
                cx={i * step}
                cy={toY(v)}
                r={i === pts.length - 1 ? 4 : 2}
                fill={i === pts.length - 1 ? "#7c3aed" : "white"}
                stroke="#7c3aed"
                strokeWidth="1.5"
              />
            ))}
          </svg>
          <div className="flex justify-between text-[9px] text-slate-400 mt-1">
            {["Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc", "Jan", "Fév", "Mar"].map((m) => (
              <span key={m}>{m}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
function Kpi({ label, value, tone }) {
  const isAccent = tone === "accent";
  return (
    <div className={`rounded-lg p-2.5 ${isAccent ? "bg-slate-900 text-white" : "bg-white border border-slate-100"}`}>
      <div className={`text-[8px] tracking-[0.2em] uppercase font-bold ${isAccent ? "text-violet-300" : "text-slate-500"}`}>{label}</div>
      <div className="font-heading text-sm font-medium mt-0.5" style={{ animation: "countUp 700ms 300ms both" }}>{value}</div>
    </div>
  );
}

function LiveNetKpi() {
  const [val, setVal] = React.useState(7410);
  React.useEffect(() => {
    const amounts = [129, 87, 254, 176];
    let i = 0;
    const t = setInterval(() => {
      setVal((v) => v + amounts[i++ % amounts.length]);
    }, 3200);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="rounded-lg p-2.5 bg-slate-900 text-white" data-testid="live-net-kpi">
      <div className="text-[8px] tracking-[0.2em] uppercase font-bold text-violet-300">Revenu net</div>
      <div key={val} className="font-heading text-sm font-medium mt-0.5 euro-pop">
        {val.toLocaleString("fr-FR")} €
      </div>
    </div>
  );
}

/* ===========================
   4. Gestion clients
   =========================== */
export function ClientsMockup() {
  const clients = [
    { name: "Jean Martin", email: "jean.m@gmail.com", bookings: 4, total: "2 180 €", selected: true, avatar: "JM" },
    { name: "Sarah Petit", email: "sarah.p@yahoo.fr", bookings: 2, total: "980 €", selected: false, avatar: "SP" },
    { name: "Kevin Leroy", email: "k.leroy@outlook.com", bookings: 1, total: "459 €", selected: false, avatar: "KL" },
    { name: "Nora Benali", email: "nora.b@gmail.com", bookings: 3, total: "1 320 €", selected: false, avatar: "NB" },
  ];
  return (
    <div className="mockup-frame w-full max-w-[640px] mx-auto">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50">
        <span className="chrome-dots" />
        <div className="ml-3 flex-1 text-[10px] font-mono text-slate-400 truncate"></div>
      </div>
      <div className="grid grid-cols-5 divide-x divide-slate-100">
        {/* List */}
        <div className="col-span-3 p-4 mockup-enter">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading text-sm font-medium text-slate-900">Clients récurrents</div>
            <div className="relative">
              <Search className="h-3 w-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <input placeholder="Rechercher" className="h-7 w-28 pl-6 pr-2 rounded-md bg-slate-100 text-[10px] border-none outline-none" />
            </div>
          </div>
          <div className="space-y-1.5">
            {clients.map((c, i) => (
              <div
                key={c.name}
                className={`flex items-center gap-2.5 p-2 rounded-lg cursor-pointer transition-colors animate-stagger-row ${
                  c.selected ? "bg-violet-50 border border-violet-200" : "hover:bg-slate-50 border border-transparent"
                }`}
                style={{ "--i": i }}
              >
                <div className={`h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-semibold ${
                  c.selected ? "bg-violet-600 text-white" : "bg-slate-200 text-slate-700"
                }`}>
                  {c.avatar}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-medium text-slate-900 truncate">{c.name}</div>
                  <div className="text-[9px] text-slate-500 truncate">{c.bookings} réservations</div>
                </div>
                <div className="text-[10px] font-semibold text-slate-900">{c.total}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Detail panel */}
        <div className="col-span-2 p-4 bg-slate-50/50">
          <div className="flex flex-col items-center text-center mt-2">
            <div className="h-14 w-14 rounded-full bg-gradient-to-br from-violet-500 to-violet-700 flex items-center justify-center text-white font-heading font-semibold">
              JM
            </div>
            <div className="font-heading text-sm font-medium text-slate-900 mt-2">Jean Martin</div>
            <div className="text-[10px] text-slate-500">Client fidèle · depuis 2025</div>
          </div>
          <div className="mt-4 space-y-1.5">
            <DetailRow icon={Mail} label="jean.m@gmail.com" />
            <DetailRow icon={Phone} label="+33 6 11 22 33 44" />
            <DetailRow icon={TrendingUp} label="4 locations · 2 180 €" />
          </div>
          <button className="mt-4 w-full px-3 py-1.5 rounded-lg border border-violet-600 text-violet-600 text-[10px] font-semibold">
            Voir l'historique
          </button>
        </div>
      </div>
    </div>
  );
}
function DetailRow({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 text-[10px] text-slate-700">
      <Icon className="h-3 w-3 text-slate-400" />
      <span className="truncate">{label}</span>
    </div>
  );
}

/* ===========================
   5. Paiements
   =========================== */
export function PaymentMockup() {
  const initialTxs = [
    { client: "Jean Martin", amount: "456 €", net: "+ 401 €", ago: "Il y a 2 min" },
    { client: "Sarah Petit", amount: "870 €", net: "+ 765 €", ago: "Hier, 14:32" },
    { client: "Kevin Leroy", amount: "459 €", net: "+ 404 €", ago: "Lun. 15/02" },
    { client: "Léa Fournier", amount: "612 €", net: "+ 538 €", ago: "À l'instant" },
    { client: "Marc Dubois", amount: "298 €", net: "+ 262 €", ago: "Il y a 12 min" },
  ];
  const [offset, setOffset] = useState(0);
  const [balance, setBalance] = useState(7410);
  const [toast, setToast] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setOffset((o) => (o + 1) % initialTxs.length);
      setBalance((b) => b + Math.floor(Math.random() * 40) + 20);
      setToast((t) => t + 1);
    }, 5200);
    return () => clearInterval(id);
  }, []);
  const txs = [0, 1, 2].map((i) => ({ ...initialTxs[(offset + i) % initialTxs.length], isNew: i === 0 }));
  return (
    <div className="mockup-frame w-full max-w-[640px] mx-auto">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50">
        <span className="chrome-dots" />
        <div className="ml-3 flex-1 text-[10px] font-mono text-slate-400 truncate"></div>
      </div>

      <div className="p-5 relative mockup-enter">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-violet-600">Paiements</div>
            <div className="font-heading text-lg font-medium text-slate-900">Transactions récentes</div>
          </div>
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-slate-900 text-white text-[10px] font-semibold">
            <div className="h-5 w-7 rounded-sm bg-[#635BFF] flex items-center justify-center text-[9px] font-bold">S</div>
            Stripe
          </div>
        </div>

        {/* Balance card */}
        <div className="mt-4 rounded-xl p-4 bg-gradient-to-br from-slate-900 via-violet-950 to-slate-900 text-white relative overflow-hidden">
          <div className="absolute -right-10 -bottom-10 w-40 h-40 rounded-full bg-violet-500/20 blur-2xl animate-glow-breathe" />
          <div className="relative">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-violet-300">Solde disponible</div>
            <div key={`bal-${balance}`} className="font-heading text-2xl font-medium mt-1 animate-flip-up">
              {balance.toLocaleString("fr-FR")},00 €
            </div>
            <div className="text-[10px] text-slate-300 mt-0.5">Virement prévu le 20/02 · IBAN ••7293</div>
          </div>
        </div>

        {/* Transactions */}
        <div className="mt-4 space-y-2">
          {txs.map((t, i) => (
            <div
              key={`tx-${offset}-${i}`}
              className="flex items-center gap-3 p-2.5 rounded-xl border border-slate-100 bg-white animate-stagger-row"
              style={{ "--i": i }}
            >
              <div className="h-8 w-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center relative">
                <CheckCircle2 className="h-4 w-4" />
                {t.isNew && (
                  <span className="absolute inset-0 rounded-full border-2 border-emerald-400/70 animate-ring-expand" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-[11px] font-medium text-slate-900 truncate">{t.client}</div>
                  {t.isNew && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-violet-600 text-white text-[8px] font-bold animate-badge-breathe">
                      <span className="h-1 w-1 rounded-full bg-white" /> NOUVEAU
                    </span>
                  )}
                </div>
                <div className="text-[9px] text-slate-500">{t.ago} · Payé via Stripe</div>
              </div>
              <div className="text-right">
                <div className="text-[11px] font-semibold text-slate-900">{t.amount}</div>
                <div className="text-[9px] font-medium text-violet-600">{t.net} net</div>
              </div>
            </div>
          ))}
        </div>

        {/* Toast notification overlay */}
        <div key={`toast-${toast}`} className="absolute -top-4 right-4 glass-card rounded-xl p-3 pr-4 flex items-center gap-3 w-[240px]"
             style={{ animation: "slideInRight 600ms cubic-bezier(0.22,1,0.36,1) both" }}>
          <div className="h-8 w-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <CheckCircle2 className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[11px] font-semibold text-slate-900">Paiement reçu</div>
            <div className="text-[9px] text-slate-500">{txs[0].client} · {txs[0].amount}</div>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ===========================
   6. Contrat automatique
   =========================== */
export function ContractMockup() {
  return (
    <div className="mockup-frame w-full max-w-[640px] mx-auto">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50">
        <span className="chrome-dots" />
        <div className="ml-3 flex-1 text-[10px] font-mono text-slate-400 truncate"></div>
      </div>
      <div className="p-5 mockup-enter">
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading text-base font-medium text-slate-900">Contrat de location</div>
          <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold">Signé</span>
        </div>
        <div className="space-y-3">
          <div className="rounded-lg border border-slate-100 p-3">
            <div className="text-[8px] tracking-[0.2em] uppercase font-bold text-violet-600">Parties</div>
            <div className="grid grid-cols-2 gap-2 mt-2 text-[10px]">
              <div><span className="text-slate-500">Loueur :</span> <span className="font-medium">Paris Luxury Cars</span></div>
              <div><span className="text-slate-500">Client :</span> <span className="font-medium">Jean Martin</span></div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-100 p-3">
            <div className="text-[8px] tracking-[0.2em] uppercase font-bold text-violet-600">Véhicule</div>
            <div className="text-[10px] font-medium mt-1">Tesla Model 3 · AA-123-BB · 129 €/jour</div>
            <div className="text-[9px] text-slate-500">08/02/2026 → 11/02/2026 · 4 jours · Total : 516 €</div>
          </div>
          <div className="rounded-lg border border-slate-100 p-3">
            <div className="text-[8px] tracking-[0.2em] uppercase font-bold text-violet-600">Conditions</div>
            <ul className="mt-1 space-y-1 text-[9px] text-slate-600">
              <li className="flex items-center gap-1"><CheckCircle2 className="h-2.5 w-2.5 text-violet-500" /> Restitution dans l'état initial</li>
              <li className="flex items-center gap-1"><CheckCircle2 className="h-2.5 w-2.5 text-violet-500" /> Même niveau de carburant</li>
            </ul>
          </div>
        </div>
        {/* Signatures */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="rounded-lg bg-slate-50 border border-slate-100 p-2.5 text-center">
            <div className="text-[8px] tracking-[0.2em] uppercase font-bold text-slate-400">Loueur</div>
            <div className="font-heading italic text-sm text-slate-900 mt-1">M. Dubois</div>
            <div className="text-[8px] text-slate-400">08/02/2026 09:14</div>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-100 p-2.5 text-center">
            <div className="text-[8px] tracking-[0.2em] uppercase font-bold text-slate-400">Client</div>
            <div className="font-heading italic text-sm text-slate-900 mt-1">J. Martin</div>
            <div className="text-[8px] text-slate-400">08/02/2026 09:22</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===========================
   7. Signature mobile
   =========================== */
export function SignatureMockup() {
  const canvasRef = React.useRef(null);
  const drawingRef = React.useRef(false);
  const [mode, setMode] = useState("demo");
  const [signed, setSigned] = useState(false);
  const [hasInk, setHasInk] = useState(false);

  const getCtx = () => {
    const c = canvasRef.current;
    if (!c) return null;
    if (c.width !== c.clientWidth || c.height !== c.clientHeight) {
      c.width = c.clientWidth;
      c.height = c.clientHeight;
    }
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";
    return ctx;
  };
  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const down = (e) => {
    e.preventDefault();
    const ctx = getCtx();
    if (!ctx) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
    drawingRef.current = true;
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    setHasInk(true);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };
  const up = () => { drawingRef.current = false; };

  const clear = () => {
    setSigned(false);
    setHasInk(false);
    if (mode === "demo") { setMode("draw"); return; }
    const c = canvasRef.current;
    if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height);
  };
  const validate = () => { if (mode === "draw" && hasInk) setSigned(true); };

  return (
    <div className="mockup-frame w-full max-w-[320px] mx-auto rounded-[28px] border-[6px] border-slate-900 bg-white overflow-hidden">
      <div className="bg-slate-900 text-white px-4 py-2 text-center text-[10px] font-medium">Signature du contrat</div>
      <div className="p-5">
        <div className="text-[10px] text-slate-500 mb-2">Dessinez votre signature ci-dessous :</div>
        <div className="h-28 rounded-xl border-2 border-dashed border-slate-300 relative overflow-hidden">
          {mode === "demo" ? (
            <>
              <svg viewBox="0 0 240 90" className="w-full h-full">
                <path d="M 20 60 Q 40 20, 60 55 T 100 40 T 140 50 T 180 35 T 220 50" fill="none" stroke="#0f172a" strokeWidth="2" strokeLinecap="round" style={{strokeDasharray: 400, animation: "drawLine 2s cubic-bezier(0.22,1,0.36,1) forwards"}} />
              </svg>
              <div
                className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[9px] font-bold"
                style={{ animation: "scaleIn 500ms cubic-bezier(0.34,1.56,0.64,1) 2s both" }}
                data-testid="signature-signed-badge"
              >
                <CheckCircle2 className="h-2.5 w-2.5" /> Signé
              </div>
            </>
          ) : (
            <>
              <canvas
                ref={canvasRef}
                className="absolute inset-0 h-full w-full cursor-crosshair"
                style={{ touchAction: "none" }}
                onPointerDown={down}
                onPointerMove={move}
                onPointerUp={up}
                onPointerLeave={up}
                data-testid="signature-canvas"
              />
              {!hasInk && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-[10px] text-slate-400">
                  Signez ici avec votre souris ou votre doigt
                </div>
              )}
              {signed && (
                <div
                  className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[9px] font-bold"
                  style={{ animation: "scaleIn 400ms cubic-bezier(0.34,1.56,0.64,1) both" }}
                  data-testid="signature-signed-badge"
                >
                  <CheckCircle2 className="h-2.5 w-2.5" /> Signé
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={clear} className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-[10px] font-medium text-slate-600 text-center hover:border-slate-400 transition-colors" data-testid="signature-clear-btn">Effacer</button>
          <button onClick={validate} className="flex-1 px-3 py-2 rounded-lg bg-violet-600 text-white text-[10px] font-semibold text-center pulse-dot" data-testid="signature-validate-btn">Valider</button>
        </div>
        <div className="mt-3 text-[9px] text-slate-400 text-center">En validant, vous acceptez les conditions du contrat.</div>
      </div>
    </div>
  );
}

/* ===========================
   8. État des lieux digital
   =========================== */
export function InspectionMockup() {
  const zones = [
    { name: "Avant", photos: 2, ok: true },
    { name: "Arrière", photos: 1, ok: true },
    { name: "Gauche", photos: 2, ok: false },
    { name: "Droite", photos: 1, ok: true },
    { name: "Intérieur", photos: 3, ok: true },
    { name: "Tableau de bord", photos: 1, ok: true },
  ];
  return (
    <div className="mockup-frame w-full max-w-[640px] mx-auto">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50">
        <span className="chrome-dots" />
        <div className="ml-3 flex-1 text-[10px] font-mono text-slate-400 truncate"></div>
      </div>
      <div className="p-5 mockup-enter">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-violet-600">État des lieux · Départ</div>
            <div className="font-heading text-base font-medium text-slate-900">Tesla Model 3 · AA-123-BB</div>
          </div>
          <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-semibold">Étape 2/4</span>
        </div>
        {/* Progress */}
        <div className="flex gap-1 mb-4">
          {["Km", "Photos", "Dommages", "Signature"].map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1 rounded-full ${i <= 1 ? "bg-violet-600" : "bg-slate-200"}`} />
              <div className={`text-[8px] text-center mt-0.5 font-medium ${i <= 1 ? "text-violet-600" : "text-slate-400"}`}>{s}</div>
            </div>
          ))}
        </div>
        {/* Mileage + Fuel */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="rounded-lg bg-slate-50 p-2.5">
            <div className="text-[8px] uppercase font-bold text-slate-500">Kilométrage</div>
            <div className="font-heading text-lg font-medium text-slate-900 mt-0.5">45 230 km</div>
          </div>
          <div className="rounded-lg bg-slate-50 p-2.5">
            <div className="text-[8px] uppercase font-bold text-slate-500">Carburant</div>
            <div className="flex gap-1 mt-1">
              {["1/4", "1/2", "3/4", "Plein"].map((l, i) => (
                <div key={l} className={`flex-1 py-1 rounded text-[9px] font-semibold text-center ${i === 2 ? "bg-violet-600 text-white" : "bg-white border border-slate-200 text-slate-500"}`}>{l}</div>
              ))}
            </div>
          </div>
        </div>
        {/* Photo zones */}
        <div className="grid grid-cols-3 gap-2">
          {zones.map((z) => (
            <div key={z.name} className="rounded-lg border border-slate-100 p-2 text-center relative">
              <div className="text-[10px] font-medium text-slate-700">{z.name}</div>
              <div className="text-[9px] text-violet-600 font-semibold">{z.photos} photos</div>
              {z.ok && <div className="absolute top-1 right-1 h-2 w-2 rounded-full bg-emerald-500" />}
              {!z.ok && <div className="absolute top-1 right-1 h-2 w-2 rounded-full bg-amber-500" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ===========================
   10. Gestion complète
   =========================== */
export function ManagementMockup() {
  return (
    <div className="mockup-frame w-full max-w-[640px] mx-auto">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50">
        <span className="chrome-dots" />
        <div className="ml-3 flex-1 text-[10px] font-mono text-slate-400 truncate"></div>
      </div>
      <div className="p-5 mockup-enter">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-violet-600">Tableau de bord</div>
            <div className="font-heading text-base font-medium text-slate-900">Vue d'ensemble</div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[
            { label: "Véhicules", value: "4", color: "text-violet-600" },
            { label: "Réservations", value: "12", color: "text-emerald-600" },
            { label: "Contrats signés", value: "10", color: "text-blue-600" },
            { label: "Revenu net", value: "7 410 €", color: "text-slate-900" },
          ].map((k) => (
            <div key={k.label} className="rounded-lg bg-slate-50 p-2.5 text-center">
              <div className="text-[8px] uppercase font-bold text-slate-500">{k.label}</div>
              <div className={`font-heading text-sm font-medium mt-0.5 ${k.color}`}>{k.value}</div>
            </div>
          ))}
        </div>
        <div className="space-y-2">
          {[
            { title: "État des lieux", desc: "2 en attente de signature", badge: "bg-amber-100 text-amber-700" },
            { title: "Contrats", desc: "1 en attente de signature client", badge: "bg-violet-100 text-violet-700" },
          ].map((n) => (
            <div key={n.title} className="flex items-center gap-3 p-2.5 rounded-xl border border-slate-100 bg-white">
              <div className={`px-2 py-0.5 rounded-full text-[9px] font-semibold ${n.badge}`}>{n.title}</div>
              <div className="text-[10px] text-slate-600">{n.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ===========================
   Junago Copilot (IA)
   =========================== */
export function CopilotMockup() {
  const rows = [
    { name: "Tesla Model 3", pct: 92, v: "2 340 €" },
    { name: "Renault Clio", pct: 74, v: "1 480 €" },
    { name: "Peugeot 3008", pct: 58, v: "1 120 €" },
  ];
  return (
    <div className="mockup-frame h-full bg-white p-4 sm:p-5 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="h-7 w-7 rounded-lg bg-violet-600 flex items-center justify-center">
            <Sparkles className="h-3.5 w-3.5 text-white" />
          </span>
          <div>
            <div className="text-[11px] font-bold text-slate-900">Junago Copilot</div>
            <div className="text-[9px] text-slate-400">Rapport hebdomadaire</div>
          </div>
        </div>
        <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold">À jour</span>
      </div>
      <p className="text-[10px] text-slate-600 leading-relaxed italic border-l-2 border-violet-200 pl-2 mb-3">
        « Votre SUV est réservé 9 week-ends sur 10 — pensez à ajuster le tarif du samedi. »
      </p>
      <div className="space-y-2 flex-1">
        {rows.map((r, i) => (
          <div key={r.name}>
            <div className="flex items-center justify-between text-[10px] mb-0.5">
              <span className="font-semibold text-slate-700">{r.name}</span>
              <span className="text-violet-600 font-bold">{r.v}</span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-400"
                style={{ width: `${r.pct}%`, animation: `scaleIn 800ms ${200 + i * 150}ms both` }}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 rounded-lg bg-violet-50 border border-violet-100 px-2.5 py-2 text-[9px] text-violet-800 font-medium">
        Fréquence de réservation en hausse de 18 % ce mois — la demande suit vos nouveaux tarifs.
      </div>
    </div>
  );
}
